import { Button, Input, Radio, Space, Typography } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  NotificationEditor,
  NotificationEditorModel,
  NotificationFieldInfo,
  Spacing,
} from 'components/ui';
import { NotificationRecipientType } from 'entities/endpoints/form.notification.send';
import { FormListPageStore } from 'stores/local';

import s from './NotificationDrawer.module.scss';

type SendNotificationTabProps = {
  formId: number;
  store: FormListPageStore;
  onSuccess: () => void;
};

const SendNotificationTab: React.FC<SendNotificationTabProps> = ({
  formId,
  store,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [recipientType, setRecipientType] = React.useState<NotificationRecipientType>(
    NotificationRecipientType.ALL_MANAGERS
  );
  const [subject, setSubject] = React.useState<string>('');
  const [targetEmail, setTargetEmail] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [fields, setFields] = React.useState<NotificationFieldInfo[]>([]);

  const isSingleEmail = recipientType === NotificationRecipientType.SINGLE_EMAIL;

  // Создаём модель редактора один раз
  const editorModelRef = React.useRef<NotificationEditorModel | null>(null);
  if (!editorModelRef.current) {
    editorModelRef.current = new NotificationEditorModel();
  }
  const editorModel = editorModelRef.current;

  const counts = store.recipientsCounts[formId];

  // Загружаем поля формы и количество получателей при монтировании
  React.useEffect(() => {
    const loadFields = async () => {
      const columns = await store.fetchFormColumns(formId);
      setFields(columns);
    };
    loadFields();
    store.fetchRecipientsCount(formId);
  }, [formId, store]);

  const handleSend = React.useCallback(async () => {
    if (!subject.trim() || !editorModel.hasContent) {
      return;
    }

    const message = editorModel.toServerFormat;

    setLoading(true);
    const { isError } = await store.sendNotification(
      formId,
      recipientType,
      subject,
      message,
      isSingleEmail ? targetEmail : undefined,
    );

    setLoading(false);

    if (!isError) {
      setSubject('');
      setTargetEmail('');
      editorModel.reset();
      onSuccess();
    }
  }, [formId, recipientType, subject, targetEmail, isSingleEmail, editorModel, store, onSuccess]);

  return (
    <div className={s.sendTab}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Typography.Text strong>
            {t('formList.notifications.recipientType')}
          </Typography.Text>
          <Radio.Group
            value={recipientType}
            onChange={(e) => setRecipientType(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{ marginTop: 8, display: 'block' }}
          >
            <Space direction="vertical">
              <Radio value={NotificationRecipientType.ALL_MANAGERS}>
                {t('formList.notifications.allManagers')}
                {counts != null && ` (${counts.all_managers})`}
              </Radio>
              <Radio value={NotificationRecipientType.WIP_ONLY}>
                {t('formList.notifications.wipOnly')}
                {counts != null && ` (${counts.wip_only})`}
              </Radio>
              <Radio value={NotificationRecipientType.ALL_EMPLOYEES}>
                {t('formList.notifications.allEmployees', 'Все сотрудники')}
                {counts != null && ` (${counts.all_employees})`}
              </Radio>
              <Radio value={NotificationRecipientType.SINGLE_EMAIL}>
                {t('formList.notifications.singleEmail', 'Конкретный email')}
              </Radio>
            </Space>
          </Radio.Group>
          {isSingleEmail && (
            <Input
              value={targetEmail}
              onChange={(e) => setTargetEmail(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder={t('formList.notifications.targetEmailPlaceholder', 'Введите email получателя...')}
              type="email"
              style={{ marginTop: 8 }}
            />
          )}
        </div>

        <div>
          <Typography.Text strong>
            {t('formList.notifications.subject')}
          </Typography.Text>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={t('formList.notifications.subjectPlaceholder')}
            style={{ marginTop: 8 }}
          />
        </div>

        <div>
          <Typography.Text strong>
            {t('formList.notifications.message')}
          </Typography.Text>
          <div style={{ marginTop: 8 }}>
            <NotificationEditor
              model={editorModel}
              fields={fields}
              placeholder={t('formList.notifications.messagePlaceholder')}
            />
          </div>
        </div>

        <Spacing.Row>
          <Button
            type="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleSend();
            }}
            loading={loading}
            disabled={!subject.trim() || !editorModel.hasContent || (isSingleEmail && !targetEmail.trim())}
          >
            {t('formList.notifications.send')}
          </Button>
        </Spacing.Row>
      </Space>
    </div>
  );
};

export default observer(SendNotificationTab);
